import React, { useState, useEffect } from 'react';
import { Spinner } from '@t-one-internal/design-system';
import { ClockCounterClockwise, CaretDown, CaretUp } from '@phosphor-icons/react';
import apiClient from '../../../api/apiClient';
import type { EmployeeDetails } from '../../../stores/employeeProfileStore';
import { getEntityDisplayName, getFieldDisplayName, getActionDisplayName, isHiddenField, formatDisplayValue } from './auditDisplayUtils';

interface FieldChange {
  field: string;
  from?: any;
  to?: any;
}

interface AuditEvent {
  id: string;
  account_id: number;
  actor_id: string;
  actor_name: string;
  entity_type: string;
  entity_id: string;
  action: string;
  changes: FieldChange[];
  metadata?: Record<string, any>;
  ip_address?: string;
  created_at: string;
}

interface UpdatesSectionProps {
  employee: EmployeeDetails;
}

const ACTION_COLORS: Record<string, string> = {
  CREATE: 'bg-green-100 text-green-700 border-green-200',
  UPDATE: 'bg-blue-100 text-blue-700 border-blue-200',
  DELETE: 'bg-red-100 text-red-700 border-red-200',
  DEACTIVATE: 'bg-amber-100 text-amber-700 border-amber-200',
  REACTIVATE: 'bg-teal-100 text-teal-700 border-teal-200',
  TERMINATE: 'bg-red-100 text-red-700 border-red-200',
  BULK_IMPORT: 'bg-indigo-100 text-indigo-700 border-indigo-200',
};

const UpdatesSection: React.FC<UpdatesSectionProps> = ({ employee }) => {
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const employeeId = employee?.user_uuid || employee?.id;

  useEffect(() => {
    if (!employeeId) return;

    const fetchAuditLogs = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await apiClient.get(`/audit-logs`, {
          params: {
            entity_type: 'employee',
            entity_id: employeeId,
            limit: 50,
          }
        });
        const data = response.data;
        if (data && Array.isArray(data.data)) {
          setEvents(data.data);
        } else {
          setEvents([]);
        }
      } catch (err: any) {
        console.error('Failed to fetch audit logs:', err);
        setError('Failed to load activity history');
        setEvents([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAuditLogs();
  }, [employeeId]);

  const toggleExpanded = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const formatDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  const renderVal = (val: any, fieldName?: string) => {
    return formatDisplayValue(val, fieldName);
  };

  if (!employeeId) {
    return (
      <div className="p-6">
        <div className="text-center text-gray-500">No employee ID available</div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-slate-900">Activity History</h3>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center py-12 text-gray-400">
          <ClockCounterClockwise size={48} className="mb-3 text-gray-300" />
          <span className="text-sm">{error}</span>
        </div>
      ) : events.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-gray-400">
          <ClockCounterClockwise size={48} className="mb-3 text-gray-300" />
          <span className="text-sm">No activity history available</span>
        </div>
      ) : (
        <div className="space-y-3 overflow-auto">
          {events.map((event) => {
            const isExpanded = expandedIds.has(event.id);
            const colors = ACTION_COLORS[event.action] || 'bg-slate-100 text-slate-700 border-slate-200';
            return (
              <div key={event.id} className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                <div className="p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase border ${colors}`}>
                        {event.action}
                      </span>
                      <span className="text-xs font-semibold text-slate-800">
                        {event.actor_name || 'System'}{' '}
                        <span className="text-slate-400 font-normal">{getActionDisplayName(event.action).toLowerCase()}</span>{' '}
                        {getEntityDisplayName(event.entity_type)}
                      </span>
                    </div>
                    <span className="text-xs text-gray-400 whitespace-nowrap">{formatDate(event.created_at)}</span>
                  </div>

                  {event.changes && event.changes.filter(c => !isHiddenField(c.field)).length > 0 && (
                    <div className="mt-2">
                      <button
                        onClick={() => toggleExpanded(event.id)}
                        className="text-[10px] font-medium text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                      >
                        {isExpanded ? 'Hide details' : `View ${event.changes.filter(c => !isHiddenField(c.field)).length} change${event.changes.filter(c => !isHiddenField(c.field)).length > 1 ? 's' : ''}`}
                        {isExpanded ? <CaretUp size={10} /> : <CaretDown size={10} />}
                      </button>

                      {isExpanded && (
                        <div className="mt-2 rounded-lg overflow-hidden border border-slate-100">
                          <table className="w-full text-left text-xs">
                            <thead className="bg-slate-50 text-slate-500">
                              <tr>
                                <th className="px-3 py-2 font-semibold">Field</th>
                                {event.action !== 'CREATE' && <th className="px-3 py-2 font-semibold">Previous</th>}
                                <th className="px-3 py-2 font-semibold">{event.action === 'CREATE' ? 'Value' : 'New Value'}</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                              {event.changes.filter(c => !isHiddenField(c.field)).map((change, idx) => (
                                <tr key={`${change.field}-${idx}`} className="hover:bg-slate-50">
                                  <td className="px-3 py-2 font-medium text-slate-600">{getFieldDisplayName(change.field)}</td>
                                  {event.action !== 'CREATE' && (
                                    <td className="px-3 py-2 text-red-500 line-through max-w-[200px] break-words">
                                      {renderVal(change.from, change.field)}
                                    </td>
                                  )}
                                  <td className="px-3 py-2 text-green-600 font-medium max-w-[200px] break-words">
                                    {renderVal(event.action === 'DELETE' ? change.from : change.to, change.field)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default UpdatesSection;
